import { DEPENDENCY_RELATIONS, type DependencyRelation, type WorkItem, type WorkItemDetail } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Bot, GitBranch, Link2, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { CreateWorkItemDialog } from '@/components/create-work-item-dialog';
import { DefinitionList } from '@/components/page';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { ApprovalStatusBadge, ExecutionStatusBadge, StatusDot, WorkItemStatusBadge } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { intlLocale, t, tc } from '@/lib/i18n';
import { DEPENDENCY_RELATION, REVIEW_DECISION } from '@/lib/labels';
import { qk } from '@/lib/query-keys';
import { formatTokens, formatUsd } from '@/lib/usage';
import { cn } from '@/lib/utils';
import { isAgentRunning } from './chat';

export function AgentPanel({ item }: { item: WorkItemDetail }) {
  const ex = item.currentExecution;
  const running = isAgentRunning(item);
  return (
    <Section icon={<Bot />} title={t('Agent')}>
      {!item.assignedAgentId ? (
        <p className="text-sm text-muted-foreground">
          {t('No agent assigned yet. Choose one via “Edit” — or the Desk’s Lead Agent will take care of it.')}
        </p>
      ) : (
        <div className="grid gap-4">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'flex size-9 items-center justify-center rounded-full',
                running ? 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-300' : 'bg-muted text-muted-foreground',
              )}
            >
              <Bot className={cn('size-4.5', running && 'animate-soft-pulse')} />
            </div>
            <div className="min-w-0 flex-1">
              <Link
                to="/w/$workspaceId/agents/$agentId"
                params={{ workspaceId: item.workspaceId, agentId: item.assignedAgentId }}
                className="text-sm font-medium hover:text-primary hover:underline"
              >
                {item.assignedAgentName ?? t('Agent')}
              </Link>
              <div className="text-xs text-muted-foreground">
                {!running ? t('is asleep') : ex?.kind === 'follow_up' ? t('is answering in the chat') : t('is working right now')}
              </div>
            </div>
          </div>
          {item.currentActivity && (
            <div className="rounded-md bg-muted/60 px-3 py-2 text-[13px]">
              <div className="text-xs text-muted-foreground">
                {t('Latest')} · <RelativeTime value={item.currentActivity.createdAt} />
              </div>
              <div className="mt-0.5 line-clamp-3 break-words">{item.currentActivity.message}</div>
            </div>
          )}
          <DefinitionList
            className="text-[13px]"
            items={[
              {
                label: tc('noun', 'Run'),
                value: ex ? <ExecutionStatusBadge status={ex.status} /> : <span className="text-muted-foreground">{t('none active')}</span>,
              },
              ...(ex?.heartbeatAt
                ? [{ label: t('Last sign of life'), value: <RelativeTime value={ex.heartbeatAt} /> }]
                : []),
              { label: t('Worker failures'), value: t('{n} of max. {max}', { n: item.attemptCount, max: item.maxAttempts }) },
              ...(item.usage.runs
                ? [
                    {
                      label: t('Tokens used'),
                      value: (
                        <span title={t('{runs} runs · input {input} · output {output}', { runs: item.usage.runs, input: item.usage.inputTokens.toLocaleString(intlLocale), output: item.usage.outputTokens.toLocaleString(intlLocale) })}>
                          {formatTokens(item.usage.inputTokens + item.usage.outputTokens)}
                          {item.usage.reviewInputTokens + item.usage.reviewOutputTokens > 0 && (
                            <span className="text-muted-foreground">
                              {' '}
                              {t('(reviews {tokens})', { tokens: formatTokens(item.usage.reviewInputTokens + item.usage.reviewOutputTokens) })}
                            </span>
                          )}
                        </span>
                      ),
                    },
                    ...(item.usage.apiCostUsd + item.usage.subscriptionValueUsd > 0
                      ? [
                          {
                            label: t('Cost'),
                            value: (
                              <span title={t('At the provider’s list prices, including reviews')}>
                                {item.usage.apiCostUsd > 0 && t('{cost} via API', { cost: formatUsd(item.usage.apiCostUsd) })}
                                {item.usage.apiCostUsd > 0 && item.usage.subscriptionValueUsd > 0 && ' · '}
                                {item.usage.subscriptionValueUsd > 0 && (
                                  <span className="text-muted-foreground">{t('{cost} covered by subscription', { cost: formatUsd(item.usage.subscriptionValueUsd) })}</span>
                                )}
                              </span>
                            ),
                          },
                        ]
                      : []),
                  ]
                : []),
            ]}
          />
        </div>
      )}
    </Section>
  );
}

export function RelationsPanel({ item }: { item: WorkItemDetail }) {
  const ws = item.workspaceId;
  return (
    <Section
      icon={<GitBranch />}
      title={t('Related items')}
      description={t('Parent and child Work Items.')}
      actions={
        <CreateWorkItemDialog workspaceId={ws} deskId={item.deskId} parentWorkItemId={item.id} defaultAgentId={item.assignedAgentId}>
          <Button variant="ghost" size="xs" title={t('Create sub-item')}>
            <Plus /> {t('Sub-item')}
          </Button>
        </CreateWorkItemDialog>
      }
    >
      <div className="grid gap-3">
        <div>
          <div className="mb-1 text-xs font-medium text-muted-foreground">{t('Part of')}</div>
          {item.parent ? (
            <MiniItem item={item.parent} desk={item.parent.deskId !== item.deskId ? t('{item} on {desk}', { item: item.parent.itemLabel ?? t('Work Item'), desk: item.parent.deskName ?? '' }) : null} />
          ) : (
            <p className="text-[13px] text-muted-foreground">{t('— (standalone)')}</p>
          )}
        </div>
        <div>
          <div className="mb-1 text-xs font-medium text-muted-foreground">{t('Sub-items ({n})', { n: item.children.length })}</div>
          {item.children.length === 0 ? (
            <p className="text-[13px] text-muted-foreground">{t('None')}</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {item.children.map((c) => (
                <li key={c.id}>
                  <MiniItem
                    item={c}
                    desk={c.deskId !== item.deskId ? t('{item} on {desk}', { item: c.itemLabel ?? t('Work Item'), desk: c.deskName ?? '' }) : null}
                    prefix={
                      c.kind === 'review' && (
                        <span className="inline-flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground" title={c.assignedAgentName ?? undefined}>
                          <ShieldCheck className="size-3.5" />
                          {c.reviewDecision ? REVIEW_DECISION[c.reviewDecision] : t('Review')}
                        </span>
                      )
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Section>
  );
}

/** `desk`: named when the item lives on another desk (a hand-off, docs/features/work-items.md). */
function MiniItem({ item, prefix, desk }: { item: Pick<WorkItem, 'id' | 'workspaceId' | 'title' | 'status'>; prefix?: ReactNode; desk?: string | null }) {
  return (
    <Link
      to="/w/$workspaceId/work-items/$workItemId"
      params={{ workspaceId: item.workspaceId, workItemId: item.id }}
      className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] hover:bg-muted"
    >
      <StatusDot status={item.status} />
      {prefix}
      <span className="min-w-0 flex-1 truncate">{item.title}</span>
      {desk && (
        <span className="shrink-0 rounded border px-1.5 py-0.5 text-[11px] text-muted-foreground" title={t('On another desk')}>
          {desk}
        </span>
      )}
    </Link>
  );
}

export function DependenciesPanel({ item }: { item: WorkItemDetail }) {
  const remove = useApiMutation((id: string) => api.workItems.removeDependency(id), {
    invalidate: [qk.workItem(item.id)],
    success: t('Dependency removed'),
  });
  return (
    <Section
      icon={<Link2 />}
      title={t('Dependencies')}
      description={t('Links to other Work Items.')}
      actions={
        <AddDependencyDialog item={item}>
          <Button variant="ghost" size="xs">
            <Plus /> {t('Add')}
          </Button>
        </AddDependencyDialog>
      }
    >
      {item.dependencies.length === 0 ? (
        <p className="text-[13px] text-muted-foreground">{t('No dependencies.')}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {item.dependencies.map((d) => {
            const otherId = d.direction === 'outgoing' ? d.dependsOnWorkItemId : d.workItemId;
            const rel = DEPENDENCY_RELATION[d.relationType];
            return (
              <li key={d.id} className="group flex items-center gap-1">
                <div className="min-w-0 flex-1">
                  <div className="px-2 text-[11px] text-muted-foreground">
                    {d.direction === 'outgoing' ? rel?.outgoing : rel?.incoming}
                  </div>
                  <MiniItem item={{ id: otherId, workspaceId: item.workspaceId, title: d.otherTitle, status: d.otherStatus }} />
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                  aria-label={t('Remove dependency')}
                  onClick={() => remove.mutate(d.id)}
                >
                  <Trash2 />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

function AddDependencyDialog({ item, children }: { item: WorkItemDetail; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [relation, setRelation] = useState<DependencyRelation>('depends_on');
  const [q, setQ] = useState('');
  const [pick, setPick] = useState<string | null>(null);
  const candidates = useQuery({
    queryKey: qk.workItems(item.workspaceId, { q: q.trim() || undefined, limit: 30 }),
    queryFn: () => api.workItems.list(item.workspaceId, { q: q.trim() || undefined, limit: 30 }),
    enabled: open,
  });
  const add = useApiMutation(() => api.workItems.addDependency(item.id, { dependsOnWorkItemId: pick!, relationType: relation }), {
    invalidate: [qk.workItem(item.id), ...(pick ? [qk.workItem(pick)] : [])],
    success: t('Dependency added'),
    onSuccess: () => {
      setOpen(false);
      setPick(null);
      setQ('');
    },
  });
  const list = (candidates.data ?? []).filter((c) => c.id !== item.id);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('Add dependency')}</DialogTitle>
          <DialogDescription>{t('Link this Work Item to another one.')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="flex items-center gap-2 text-sm">
            <span className="shrink-0 text-muted-foreground">{t('This Work Item')}</span>
            <Select value={relation} onValueChange={(v) => setRelation(v as DependencyRelation)}>
              <SelectTrigger size="sm" className="w-auto">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DEPENDENCY_RELATIONS.map((r) => (
                  <SelectItem key={r} value={r}>
                    {DEPENDENCY_RELATION[r].outgoing}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-muted-foreground">…</span>
          </div>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Search Work Items …')} autoFocus />
          <div className="max-h-64 overflow-y-auto rounded-md border">
            {candidates.isPending ? (
              <p className="p-3 text-sm text-muted-foreground">{t('Loading …')}</p>
            ) : list.length === 0 ? (
              <p className="p-3 text-sm text-muted-foreground">{t('No matching Work Items.')}</p>
            ) : (
              list.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setPick(c.id)}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2 border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted',
                    pick === c.id && 'bg-accent text-accent-foreground',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">{c.title}</span>
                  <WorkItemStatusBadge status={c.status} />
                </button>
              ))
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t('Cancel')}
          </Button>
          <Button disabled={!pick} loading={add.isPending} onClick={() => add.mutate()}>
            {t('Add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ApprovalsPanel({ item }: { item: WorkItemDetail }) {
  const resolved = item.approvals.filter((a) => a.status !== 'pending');
  if (resolved.length === 0) return null;
  return (
    <Section icon={<ShieldCheck />} title={t('Previous approvals')}>
      <ul className="flex flex-col gap-2.5">
        {resolved.map((a) => (
          <li key={a.id} className="text-[13px]">
            <div className="flex items-center justify-between gap-2">
              <ApprovalStatusBadge status={a.status} />
              <RelativeTime value={a.resolvedAt ?? a.requestedAt} className="text-xs text-muted-foreground" />
            </div>
            <p className="mt-1 line-clamp-3 break-words">{a.description}</p>
            {a.choice && <p className="mt-1 text-xs font-medium">{t('Picked: {choice}', { choice: a.choice })}</p>}
            {a.resolutionComment && <p className="mt-1 text-xs text-muted-foreground">{t('Comment: {comment}', { comment: a.resolutionComment })}</p>}
          </li>
        ))}
      </ul>
    </Section>
  );
}

